'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@/comps/primitives/skeleton';
import type { ChartTooltipRow } from '@/domains/pages/shared/chart-tooltip';
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import type { YAxisBounds } from '@/domains/pages/shared/chart-axis';
import { buildAxisTicks, buildSegmentScalePoints, estimateLabelWidthPx, labelTransform, MAX_DOT_RADIUS_PX, scaleToPercent, selectVisibleLabels, type SegmentScalePoint, type SegmentScaleSegment } from '@/domains/pages/shared/segment-scale';
import { PAGE_DATA_NUMBER_CLASS } from '@/domains/pages/shared/typography';
import { cn } from '@/utils/shad';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

/** Reference marker drawn on the axis, e.g. the org average. */
export type SegmentScaleReference = {
  value: number;
  /** Prefix shown before the formatted value, e.g. "org". */
  label: string;
};
type SegmentScaleChartProps = {
  segments: SegmentScaleSegment[];
  bounds: YAxisBounds;
  selectedName: string;
  /** Dot colour for a segment value, e.g. against the peer median or the org average. */
  colorForValue: (value: number) => string;
  formatValue: (value: number) => string;
  formatTick: (value: number) => string;
  buildTooltipRows: (point: SegmentScalePoint) => ChartTooltipRow[];
  /** Metric noun read out in the dot's accessible name, e.g. "score". */
  metricLabel: string;
  reference?: SegmentScaleReference | null;
  onSelect?: (name: string) => void;
  className?: string;
  testId?: string;
};

/** Vertical room for one lane of labels. */
const LABEL_LANE_HEIGHT_PX = 20;
/** Gap between the axis and the first lane on either side. */
const LANE_OFFSET_PX = 14;
/** Fallback width before the container has been measured. */
const FALLBACK_WIDTH_PX = 900;
/** Org reference dot diameter. */
const REFERENCE_DOT_PX = 10;

/** Height of the tick row under the plot (mt-1 + h-3). Exported for tests. */
export const TICK_ROW_HEIGHT_PX = 16;
const aboveHeight = LABEL_LANE_HEIGHT_PX + LANE_OFFSET_PX + MAX_DOT_RADIUS_PX;
const belowHeight = MAX_DOT_RADIUS_PX + LANE_OFFSET_PX + LABEL_LANE_HEIGHT_PX;
const teamLabelTop = 0;
const orgLabelTop = aboveHeight + MAX_DOT_RADIUS_PX + LANE_OFFSET_PX;

/**
 * Fixed plot area height, shared with the loading state so the card keeps its height
 * while data arrives.
 */
export const SEGMENT_SCALE_PLOT_HEIGHT_PX = aboveHeight + belowHeight;

/** Halo on the selected dot: two rings fading outwards plus a soft glow. */
function selectedDotShadow(color: string): string {
  return `0 0 0 4px ${color}38, 0 0 0 9px ${color}1A, 0 0 22px 6px ${color}66`;
}
export function SegmentScaleChart(t0) {
  const $ = _c(114);
  const {
    segments,
    bounds,
    selectedName,
    colorForValue,
    formatValue,
    formatTick,
    buildTooltipRows,
    metricLabel,
    reference,
    onSelect,
    className,
    testId: t1
  } = t0;
  const testId = t1 === undefined ? "segment-scale-chart" : t1;
  const plotRef = useRef(null);
  const [width, setWidth] = useState(FALLBACK_WIDTH_PX);
  const [hoveredName, setHoveredName] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      x: 0,
      y: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [tooltipPos, setTooltipPos] = useState(t2);
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const plot = plotRef.current;
      if (!plot) {
        return;
      }
      const measure = () => setWidth(plot.getBoundingClientRect().width || FALLBACK_WIDTH_PX);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(plot);
      return () => observer.disconnect();
    };
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useLayoutEffect(t3, t4);
  let t5;
  if ($[3] !== bounds.max || $[4] !== bounds.min || $[5] !== segments) {
    t5 = buildSegmentScalePoints(segments, bounds.min, bounds.max);
    $[3] = bounds.max;
    $[4] = bounds.min;
    $[5] = segments;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const points = t5;
  let t6;
  if ($[7] !== formatValue || $[8] !== points || $[9] !== selectedName || $[10] !== width) {
    let t7;
    if ($[12] !== formatValue || $[13] !== selectedName) {
      t7 = point => {
        const isSelected = point.name === selectedName;
        return {
          name: point.name,
          widthPx: estimateLabelWidthPx(point.displayName, isSelected ? formatValue(point.value) : ""),
          percent: point.percent,
          priority: isSelected ? Number.POSITIVE_INFINITY : point.weight
        };
      };
      $[12] = formatValue;
      $[13] = selectedName;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    const candidates = points.map(t7);
    t6 = selectVisibleLabels(candidates, width);
    $[7] = formatValue;
    $[8] = points;
    $[9] = selectedName;
    $[10] = width;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const visibleLabels = t6;
  let t7;
  if ($[15] !== bounds.max || $[16] !== bounds.min || $[17] !== bounds.stepSize) {
    t7 = buildAxisTicks(bounds.min, bounds.max, bounds.stepSize);
    $[15] = bounds.max;
    $[16] = bounds.min;
    $[17] = bounds.stepSize;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const ticks = t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (name, event) => {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
      setHoveredName(name);
    };
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const showTooltip = t8;
  let t9;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setHoveredName(null);
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const hideTooltip = t9;
  let T0;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  if ($[21] !== bounds.max || $[22] !== bounds.min || $[23] !== buildTooltipRows || $[24] !== className || $[25] !== colorForValue || $[26] !== formatTick || $[27] !== formatValue || $[28] !== hoveredName || $[29] !== metricLabel || $[30] !== onSelect || $[31] !== points || $[32] !== reference || $[33] !== selectedName || $[34] !== testId || $[35] !== ticks || $[36] !== tooltipPos || $[37] !== visibleLabels) {
    t18 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t19;
      if ($[48] !== hoveredName) {
        t19 = point_0 => point_0.name === hoveredName;
        $[48] = hoveredName;
        $[49] = t19;
      } else {
        t19 = $[49];
      }
      const hovered = points.find(t19);
      let t20;
      let t21;
      let t22;
      let t23;
      let t24;
      if ($[50] !== bounds.max || $[51] !== bounds.min || $[52] !== className || $[53] !== formatValue || $[54] !== points.length || $[55] !== reference || $[56] !== testId) {
        const referencePercent = reference != null ? scaleToPercent(reference.value, bounds.min, bounds.max) : null;
        if (points.length === 0) {
          t18 = null;
          break bb0;
        }
        if ($[64] !== className) {
          t14 = cn("relative", className);
          $[64] = className;
          $[65] = t14;
        } else {
          t14 = $[65];
        }
        t15 = testId;
        t20 = plotRef;
        t21 = "relative";
        if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
          t22 = {
            height: SEGMENT_SCALE_PLOT_HEIGHT_PX
          };
          $[66] = t22;
        } else {
          t22 = $[66];
        }
        if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
          t23 = <div className="absolute right-0 left-0 h-px bg-border" style={{
            top: aboveHeight
          }} />;
          $[67] = t23;
        } else {
          t23 = $[67];
        }
        t24 = reference != null && referencePercent != null ? <><div className="pointer-events-none absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-foreground bg-card" style={{
            left: `${referencePercent}%`,
            top: aboveHeight,
            width: REFERENCE_DOT_PX,
            height: REFERENCE_DOT_PX,
            zIndex: 3
          }} /><span className={cn("absolute text-[11px] leading-none whitespace-nowrap text-muted-foreground", PAGE_DATA_NUMBER_CLASS)} style={{
            left: `${referencePercent}%`,
            top: orgLabelTop,
            transform: labelTransform(referencePercent)
          }}>{reference.label} {formatValue(reference.value)}</span></> : null;
        $[50] = bounds.max;
        $[51] = bounds.min;
        $[52] = className;
        $[53] = formatValue;
        $[54] = points.length;
        $[55] = reference;
        $[56] = testId;
        $[57] = t14;
        $[58] = t15;
        $[59] = t20;
        $[60] = t21;
        $[61] = t22;
        $[62] = t23;
        $[63] = t24;
      } else {
        t14 = $[57];
        t15 = $[58];
        t20 = $[59];
        t21 = $[60];
        t22 = $[61];
        t23 = $[62];
        t24 = $[63];
      }
      let t25;
      if ($[68] !== colorForValue || $[69] !== formatValue || $[70] !== metricLabel || $[71] !== onSelect || $[72] !== points || $[73] !== selectedName || $[74] !== visibleLabels) {
        let t26;
        if ($[76] !== colorForValue || $[77] !== formatValue || $[78] !== metricLabel || $[79] !== onSelect || $[80] !== selectedName || $[81] !== visibleLabels) {
          t26 = point_1 => {
            const color = colorForValue(point_1.value);
            const isSelected_0 = point_1.name === selectedName;
            const showLabel = visibleLabels.has(point_1.name);
            return <div key={point_1.name}><button type="button" aria-label={`${point_1.displayName}, ${metricLabel} ${formatValue(point_1.value)}`} className="absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full p-0 transition-transform duration-150 hover:scale-110" style={{
                left: `${point_1.percent}%`,
                top: aboveHeight,
                width: point_1.diameterPx,
                height: point_1.diameterPx,
                backgroundColor: isSelected_0 ? `${color}CC` : `${color}55`,
                border: `${isSelected_0 ? 2 : 1.5}px solid ${color}`,
                boxShadow: isSelected_0 ? selectedDotShadow(color) : undefined,
                zIndex: isSelected_0 ? 2 : 1
              }} onMouseEnter={event_0 => showTooltip(point_1.name, event_0)} onFocus={event_1 => showTooltip(point_1.name, event_1)} onMouseLeave={hideTooltip} onBlur={hideTooltip} onClick={onSelect ? () => onSelect(point_1.name) : undefined} />{showLabel ? isSelected_0 ? <span className="absolute text-xs leading-none font-semibold whitespace-nowrap text-foreground" style={{
                left: `${point_1.percent}%`,
                top: teamLabelTop,
                transform: labelTransform(point_1.percent)
              }}>{point_1.displayName}{" "}<span className={cn("font-semibold", PAGE_DATA_NUMBER_CLASS)} style={{
                  color
                }}>{formatValue(point_1.value)}</span></span> : <span className="absolute text-[11px] leading-none whitespace-nowrap text-muted-foreground" style={{
                left: `${point_1.percent}%`,
                top: teamLabelTop,
                transform: labelTransform(point_1.percent)
              }}>{point_1.displayName}</span> : null}</div>;
          };
          $[76] = colorForValue;
          $[77] = formatValue;
          $[78] = metricLabel;
          $[79] = onSelect;
          $[80] = selectedName;
          $[81] = visibleLabels;
          $[82] = t26;
        } else {
          t26 = $[82];
        }
        t25 = points.map(t26);
        $[68] = colorForValue;
        $[69] = formatValue;
        $[70] = metricLabel;
        $[71] = onSelect;
        $[72] = points;
        $[73] = selectedName;
        $[74] = visibleLabels;
        $[75] = t25;
      } else {
        t25 = $[75];
      }
      if ($[83] !== t20 || $[84] !== t21 || $[85] !== t22 || $[86] !== t23 || $[87] !== t24 || $[88] !== t25) {
        t16 = <div ref={t20} className={t21} style={t22}>{t23}{t24}{t25}</div>;
        $[83] = t20;
        $[84] = t21;
        $[85] = t22;
        $[86] = t23;
        $[87] = t24;
        $[88] = t25;
        $[89] = t16;
      } else {
        t16 = $[89];
      }
      let t26;
      if ($[90] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = cn("relative mt-1 h-3 text-[10px] leading-none text-muted-foreground", PAGE_DATA_NUMBER_CLASS);
        $[90] = t26;
      } else {
        t26 = $[90];
      }
      let t27;
      if ($[91] !== bounds.max || $[92] !== bounds.min || $[93] !== formatTick || $[94] !== ticks) {
        let t28;
        if ($[96] !== bounds.max || $[97] !== bounds.min || $[98] !== formatTick) {
          t28 = tick => <span key={tick} className="absolute whitespace-nowrap" style={{
            left: `${scaleToPercent(tick, bounds.min, bounds.max)}%`,
            transform: labelTransform(scaleToPercent(tick, bounds.min, bounds.max))
          }}>{formatTick(tick)}</span>;
          $[96] = bounds.max;
          $[97] = bounds.min;
          $[98] = formatTick;
          $[99] = t28;
        } else {
          t28 = $[99];
        }
        t27 = ticks.map(t28);
        $[91] = bounds.max;
        $[92] = bounds.min;
        $[93] = formatTick;
        $[94] = ticks;
        $[95] = t27;
      } else {
        t27 = $[95];
      }
      if ($[100] !== t27) {
        t17 = <div className={t26}>{t27}</div>;
        $[100] = t27;
        $[101] = t17;
      } else {
        t17 = $[101];
      }
      T0 = FloatingTooltip;
      t10 = hovered != null;
      t11 = tooltipPos.x;
      t12 = tooltipPos.y;
      t13 = hovered ? <ChartTooltipPanel title={hovered.displayName} rows={buildTooltipRows(hovered)} /> : null;
    }
    $[21] = bounds.max;
    $[22] = bounds.min;
    $[23] = buildTooltipRows;
    $[24] = className;
    $[25] = colorForValue;
    $[26] = formatTick;
    $[27] = formatValue;
    $[28] = hoveredName;
    $[29] = metricLabel;
    $[30] = onSelect;
    $[31] = points;
    $[32] = reference;
    $[33] = selectedName;
    $[34] = testId;
    $[35] = ticks;
    $[36] = tooltipPos;
    $[37] = visibleLabels;
    $[38] = T0;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t14;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
  } else {
    T0 = $[38];
    t10 = $[39];
    t11 = $[40];
    t12 = $[41];
    t13 = $[42];
    t14 = $[43];
    t15 = $[44];
    t16 = $[45];
    t17 = $[46];
    t18 = $[47];
  }
  if (t18 !== Symbol.for("react.early_return_sentinel")) {
    return t18;
  }
  let t19;
  if ($[102] !== T0 || $[103] !== t10 || $[104] !== t11 || $[105] !== t12 || $[106] !== t13) {
    t19 = <T0 open={t10} x={t11} y={t12}>{t13}</T0>;
    $[102] = T0;
    $[103] = t10;
    $[104] = t11;
    $[105] = t12;
    $[106] = t13;
    $[107] = t19;
  } else {
    t19 = $[107];
  }
  let t20;
  if ($[108] !== t14 || $[109] !== t15 || $[110] !== t16 || $[111] !== t17 || $[112] !== t19) {
    t20 = <div className={t14} data-testid={t15}>{t16}{t17}{t19}</div>;
    $[108] = t14;
    $[109] = t15;
    $[110] = t16;
    $[111] = t17;
    $[112] = t19;
    $[113] = t20;
  } else {
    t20 = $[113];
  }
  return t20;
}

/** Loading state for the scale chart: same box height, one small placeholder bar. */
export function SegmentScaleChartSkeleton(t0) {
  const $ = _c(6);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    testId: t2
  } = t1;
  const testId = t2 === undefined ? "segment-scale-chart-skeleton" : t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      height: SEGMENT_SCALE_PLOT_HEIGHT_PX + TICK_ROW_HEIGHT_PX
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Skeleton className="absolute left-0 h-2 w-48 -translate-y-1/2 rounded-full" style={{
      top: SEGMENT_SCALE_PLOT_HEIGHT_PX / 2
    }} />;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== testId) {
    t5 = <div className="relative" style={t3} data-testid={testId}>{t4}</div>;
    $[4] = testId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}